/**
 * Pantalla de Dashboard: primera pantalla "real" del cliente (#45).
 *
 * Reemplaza el placeholder de Inicio (ruta `/`). Muestra los KPIs equivalentes al
 * dashboard del SSR (total, pendientes, completadas, entregadas, canceladas y
 * tiempo promedio) consumiendo `GET /api/v1/core/dashboard` con TanStack Query
 * (`useDashboardStats`), y una métrica avanzada (efectividad de entregas) que
 * solo se pide/muestra con el permiso `advanced_stats` (paridad con la sección
 * "Análisis" del SSR), vía `useEffectiveness`.
 *
 * Presentacional + orquestación de hooks: los datos llegan de los hooks de datos
 * (no hay `fetch` aquí); el componente solo decide qué pintar en cada estado
 * (cargando / error / datos), sin pantallas en blanco (`docs/frontend_
 * conventions.md`, Datos). La sede activa viaja por `X-Sede-Id` (lib/api), así que
 * los KPIs son los de la sede seleccionada.
 */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SectionHeading,
} from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { useDashboardStats } from "../hooks/useDashboardStats";
import { useEffectiveness } from "../hooks/useEffectiveness";
import { KpiCard } from "./KpiCard";
import { QuickActions } from "./QuickActions";
import { SystemInfo } from "./SystemInfo";

/** Permiso que gobierna las métricas avanzadas (igual que el menú "Análisis"). */
const ADVANCED_STATS_PERMISSION = "advanced_stats";

/** Mensaje de error legible: usa el del `ApiError`/`Error`, con un texto de respaldo. */
function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudieron cargar los datos.";
}

export function DashboardScreen() {
  const { hasPermission } = useAuth();
  const canSeeAdvanced = hasPermission(ADVANCED_STATS_PERMISSION);

  const stats = useDashboardStats();
  // Solo se pide la métrica avanzada con el permiso correspondiente.
  const effectiveness = useEffectiveness(canSeeAdvanced);

  return (
    <div className="flex flex-col gap-6">
      <SectionHeading as="h1" title="Inicio" />

      <section aria-label="Indicadores de órdenes">
        {stats.isPending ? (
          <p className="text-sm text-slate-500" role="status">
            Cargando indicadores…
          </p>
        ) : stats.isError ? (
          <Card>
            <CardContent>
              <p className="text-sm text-red-600" role="alert">
                {errorMessage(stats.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard
              label="Total de órdenes"
              value={stats.data.total}
              icon="fas fa-clipboard-list"
            />
            <KpiCard label="Pendientes" value={stats.data.pending} icon="fas fa-clock" />
            <KpiCard label="Completadas" value={stats.data.completed} icon="fas fa-cogs" />
            <KpiCard label="Entregadas" value={stats.data.delivered} icon="fas fa-truck" />
            <KpiCard label="Anuladas" value={stats.data.cancelled} icon="fas fa-ban" />
            <KpiCard
              label="Tiempo promedio"
              value={stats.data.avg_duration}
              icon="fas fa-stopwatch"
            />
          </div>
        )}
      </section>

      <section
        aria-label="Acciones e información del sistema"
        className="grid grid-cols-1 gap-6 lg:grid-cols-2"
      >
        <QuickActions />
        <SystemInfo />
      </section>

      {canSeeAdvanced && (
        <Card>
          <CardHeader>
            <CardTitle>Efectividad de entregas</CardTitle>
          </CardHeader>
          <CardContent>
            {effectiveness.isPending ? (
              <p className="text-sm text-slate-500" role="status">
                Cargando efectividad…
              </p>
            ) : effectiveness.isError ? (
              <p className="text-sm text-red-600" role="alert">
                {errorMessage(effectiveness.error)}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                  label="Efectividad"
                  value={`${effectiveness.data.effectiveness_percentage}%`}
                  icon="fas fa-bullseye"
                />
                <KpiCard
                  label="A tiempo"
                  value={effectiveness.data.on_time}
                  icon="fas fa-check"
                />
                <KpiCard
                  label="Anticipadas"
                  value={effectiveness.data.early}
                  icon="fas fa-forward"
                />
                <KpiCard
                  label="Tardías"
                  value={effectiveness.data.late}
                  icon="fas fa-hourglass-end"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
